import { useEffect, useRef, useState } from 'react';

const FAQS = [
  {
    q: 'Is Barkfy just another marketing agency?',
    a: 'Absolutely not. Traditional agencies focus on spending your money on traffic without qualifying leads, making you waste both time and capital. We are a Commercial Infrastructure. While we have elite strategists for branding and positioning, that\'s just the baseline. Our real value is finding the right client, qualifying them, and putting you \'face-to-face\' only with high-ticket opportunities ready to close.',
  },
  {
    q: 'What makes your marketing team different?',
    a: 'We don\'t just run ads; we protect your investment. We have a native US team that handles the entire lifecycle: we pick up the phone, qualify the lead, and schedule the appointment. We move the ball to the goal line so you can just score. Plus, we keep your clients active for referrals long after the project is done.',
  },
  {
    q: 'How do you help me close $100k+ contracts?',
    a: 'We provide the environment for success. You gain access to our Downtown Orlando headquarters, bank financing for your clients, and a sales force that handles the calls you\'re too busy to take.',
  },
  {
    q: "What is the 'Elite Business Hub'?",
    a: "It's our ecosystem of $30M+. You're no longer working alone. You're backed by the Barkfy Group, utilizing our established reputation and physical infrastructure to dominate the market.",
  },
];

function AccordionItem({
  faq,
  index,
  isOpen,
  onToggle,
  visible,
}: {
  faq: { q: string; a: string };
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  visible: boolean;
}) {
  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${index * 100}ms, transform 0.6s ease ${index * 100}ms`,
        borderBottom: '1px solid rgba(20,60,60,0.12)',
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-start justify-between gap-4 cursor-pointer"
        style={{ padding: '24px 0', background: 'transparent', border: 'none', minHeight: '72px' }}
      >
        {/* Number + Question */}
        <div className="flex items-start gap-4">
          <span
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '11px',
              color: '#DC5014',
              letterSpacing: '0.12em',
              minWidth: '24px',
              paddingTop: '3px',
              flexShrink: 0,
            }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
          <span
            style={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 600,
              fontSize: 'clamp(16px, 2vw, 20px)',
              color: '#0A0A0A',
              lineHeight: 1.4,
            }}
          >
            {faq.q}
          </span>
        </div>

        {/* Toggle icon */}
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: `1px solid ${isOpen ? '#DC5014' : 'rgba(20,60,60,0.2)'}`,
            background: isOpen ? '#DC5014' : 'transparent',
            transition: 'all 0.3s ease',
            marginTop: '0px',
          }}
        >
          <i
            className={isOpen ? 'ri-subtract-line' : 'ri-add-line'}
            style={{
              fontSize: '18px',
              color: isOpen ? '#FFFFFF' : '#143C3C',
              transition: 'color 0.3s ease',
            }}
          />
        </div>
      </button>

      {/* Answer — animated */}
      <div
        style={{
          maxHeight: isOpen ? '500px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div style={{ paddingBottom: '28px', paddingLeft: '28px' }}>
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 300,
              fontSize: '16px',
              color: 'rgba(10,10,10,0.6)',
              lineHeight: 1.85,
              maxWidth: '760px',
            }}
          >
            {faq.a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const toggle = (i: number) => {
    setOpenIndex((prev) => (prev === i ? null : i));
  };

  return (
    <section
      id="faq"
      ref={ref}
      className="px-5 sm:px-8 md:px-[80px]"
      style={{ background: '#FFFFFF', paddingTop: '80px', paddingBottom: '80px' }}
    >
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            marginBottom: '56px',
          }}
          className="flex flex-col items-center text-center"
        >
          <div className="flex items-center justify-center" style={{ gap: '14px', marginBottom: '28px' }}>
            <div style={{ width: '32px', height: '1px', background: '#DC5014', flexShrink: 0 }} />
            <span
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '11px',
                color: '#DC5014',
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
              }}
            >
              OBJECTION KILLERS
            </span>
            <div style={{ width: '32px', height: '1px', background: '#DC5014', flexShrink: 0 }} />
          </div>

          <h2
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(28px, 6vw, 52px)',
              fontWeight: 700,
              color: '#0A0A0A',
              lineHeight: 1.15,
              maxWidth: '560px',
              margin: '0 auto 20px',
            }}
          >
            Every Question.
            <br />Answered Honestly.
          </h2>

          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 300,
              fontSize: '16px',
              color: 'rgba(10,10,10,0.5)',
              maxWidth: '480px',
              lineHeight: 1.8,
            }}
          >
            We know you have doubts. Here&apos;s the truth — no fluff, no sales pitch.
          </p>
        </div>

        {/* Accordion */}
        <div style={{ borderTop: '1px solid rgba(20,60,60,0.12)' }}>
          {FAQS.map((faq, i) => (
            <AccordionItem
              key={faq.q}
              faq={faq}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
              visible={visible}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease 500ms, transform 0.7s ease 500ms',
            marginTop: '56px',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 300,
              fontSize: '15px',
              color: 'rgba(10,10,10,0.45)',
              marginBottom: '24px',
            }}
          >
            Still have questions? Let&apos;s talk directly.
          </p>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-3"
            style={{
              background: '#DC5014',
              color: '#FFFFFF',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '12px',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              padding: '0 48px',
              height: '52px',
              minHeight: '52px',
              borderRadius: '4px',
              textDecoration: 'none',
              transition: 'background 0.3s ease, transform 0.3s ease',
              display: 'inline-flex',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = '#C44410';
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = '#DC5014';
              (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
            }}
          >
            <i className="ri-calendar-check-line" style={{ fontSize: '14px' }} />
            APPLY TO JOIN THE HUB
          </a>
        </div>

      </div>
    </section>
  );
}
